import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';

interface ProfileRow {
  id: string;
  full_name: string;
}

interface PackageRow {
  id: string;
  student_id: string | null;
  type: string | null;
  name: string | null;
  total_sessions: number;
  used_sessions: number;
  payment_status: string;
  start_date: string | null;
  end_date: string | null;
  note: string | null;
}

interface PaketModalProps {
  open: boolean;
  onClose: () => void;
  packageRow: PackageRow | null;
  profiles: ProfileRow[];
}

export default function PaketModal({ open, onClose, packageRow, profiles }: PaketModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalSessions, setTotalSessions] = useState(0);
  const [usedSessions, setUsedSessions] = useState(0);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSaving(false);
    }
    setTotalSessions(packageRow?.total_sessions ?? 0);
    setUsedSessions(packageRow?.used_sessions ?? 0);
  }, [open, packageRow]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const student_id = (fd.get('student_id') as string || '').trim() || null;
    const type = (fd.get('type') as string || '').trim() || null;
    const name = (fd.get('name') as string || '').trim() || null;
    const total = parseInt(fd.get('total_sessions') as string || '0', 10) || 0;
    const used = parseInt(fd.get('used_sessions') as string || '0', 10) || 0;
    const payment_status = (fd.get('payment_status') as string || '').trim() || 'bekliyor';
    const start_date = (fd.get('start_date') as string || '').trim() || null;
    const end_date = (fd.get('end_date') as string || '').trim() || null;
    const note = (fd.get('note') as string || '').trim() || null;

    if (!student_id) {
      setError('Lütfen öğrenci seçin.');
      return;
    }
    if (!name) {
      setError('Lütfen paket adı girin.');
      return;
    }
    if (total <= 0) {
      setError('Toplam seans sayısı 0 dan büyük olmalı.');
      return;
    }
    if (used > total) {
      setError('Kullanılan seans, toplam seansı aşamaz.');
      return;
    }

    const remaining = total - used;

    setError(null);
    setSaving(true);

    try {
      const payload = {
        student_id,
        type,
        name,
        total_sessions: total,
        used_sessions: used,
        remaining_sessions: remaining,
        payment_status,
        start_date,
        end_date,
        note,
      };

      if (packageRow) {
        const { error: updateError } = await supabase
          .from('zehrahoca_packages')
          .update(payload)
          .eq('id', packageRow.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('zehrahoca_packages')
          .insert(payload);
        if (insertError) throw insertError;
      }

      form.reset();
      window.dispatchEvent(new Event('zehrahoca:odeme-changed'));
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Paket kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={packageRow ? 'Paket Düzenle' : 'Yeni Paket Ekle'}
      subtitle={packageRow ? 'Paket bilgilerini güncelleyin.' : 'Öğrenciye yeni bir ders paketi tanımlayın.'}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Öğrenci</label>
            <select
              name="student_id"
              defaultValue={packageRow?.student_id ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Öğrenci seçin</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>{p.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Paket Adı</label>
            <input
              type="text"
              name="name"
              defaultValue={packageRow?.name ?? ''}
              required
              placeholder="Örn: 10 Derslik Özel Paket"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Paket Tipi</label>
            <select
              name="type"
              defaultValue={packageRow?.type ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Seçin</option>
              <option value="birebir">Birebir Ders</option>
              <option value="grup">Grup Dersi</option>
              <option value="kamp">Kamp</option>
              <option value="deneme">Deneme Dersi</option>
              <option value="abonelik">Aylık Abonelik</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Toplam Seans</label>
            <input
              type="number"
              name="total_sessions"
              min={1}
              defaultValue={packageRow?.total_sessions ?? ''}
              onChange={(e) => setTotalSessions(parseInt(e.target.value, 10) || 0)}
              placeholder="10"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Kullanılan Seans</label>
            <input
              type="number"
              name="used_sessions"
              min={0}
              defaultValue={packageRow?.used_sessions ?? 0}
              onChange={(e) => setUsedSessions(parseInt(e.target.value, 10) || 0)}
              placeholder="0"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ödeme Durumu</label>
            <select
              name="payment_status"
              defaultValue={packageRow?.payment_status ?? 'bekliyor'}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="odendi">Ödendi</option>
              <option value="kismi">Kısmi Ödendi</option>
              <option value="bekliyor">Bekliyor</option>
              <option value="gecikti">Gecikti</option>
            </select>
          </div>

          <div className="sm:col-span-2 rounded-lg bg-background-50 px-4 py-3 text-sm text-foreground-600">
            <span className="font-semibold text-foreground-800">Kalan seans:</span>{' '}
            {Math.max(totalSessions - usedSessions, 0)} seans
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Başlangıç Tarihi</label>
            <input
              type="date"
              name="start_date"
              defaultValue={packageRow?.start_date ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Bitiş Tarihi</label>
            <input
              type="date"
              name="end_date"
              defaultValue={packageRow?.end_date ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Not</label>
            <textarea
              name="note"
              defaultValue={packageRow?.note ?? ''}
              rows={2}
              maxLength={500}
              placeholder="Ek not..."
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap">İptal</button>
          <button type="submit" disabled={saving} className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap">
            {saving ? 'Kaydediliyor...' : packageRow ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </form>
    </Modal>
  );
}